import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@cortex/web-design-system/components/ui/Avatar';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import type {
  CuttysarkLiveCandidate,
  CuttysarkDivision,
  CuttysarkDivisionResult,
} from '@cortex/domain-product-innovation-lab-types';
import { candidatePhoto } from './cuttysark-helpers.js';

/** @graph-connects none */
interface ResultsViewProps {
  divisions: CuttysarkDivision[];
  candidates: CuttysarkLiveCandidate[];
  /** API が閲覧者に見せてよいと判断した範囲の結果（未発表の行は含まれていない）。 */
  results: CuttysarkDivisionResult[];
  /** 枠の境目の同票を知らせる（運営画面のみ）。 */
  showTie?: boolean;
}

/**
 * 部門ごとの結果（順位の高い順）と匿名の応援コメント。
 * 見せる範囲の判断は API がしており、ここは返ってきた行をそのまま並べる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の部門ごとの順位・得票・勝ち上がり・応援コメントを表示する
 * @graph-connects candidatePhoto [calls] 表示する写真の決定
 */
export function ResultsView({
  divisions,
  candidates,
  results,
  showTie = false,
}: ResultsViewProps): ReactNode {
  const { t } = useTranslation();
  const byEmail = new Map(candidates.map((c) => [c.email, c]));

  return (
    <div className="space-y-4">
      {divisions.map((division) => {
        const entries = results.find((r) => r.divisionId === division.id)?.entries ?? [];
        return (
          <section key={division.id} className="space-y-2">
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {division.name}
            </h3>
            {entries.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('cuttysark.results.hidden')}
              </p>
            ) : (
              <ul className="space-y-2">
                {entries.map((entry) => {
                  const candidate = byEmail.get(entry.candidateEmail);
                  return (
                    <li
                      key={entry.candidateEmail}
                      className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3"
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="w-14 text-lg font-bold text-[var(--color-text-primary)]">
                          {t('cuttysark.results.rank', { rank: entry.rank })}
                        </span>
                        <Avatar
                          src={candidate ? candidatePhoto(candidate) || undefined : undefined}
                          name={candidate?.name ?? entry.candidateEmail}
                          size="md"
                        />
                        <div className="min-w-0 flex-1">
                          {candidate !== undefined && candidate.title !== '' && (
                            <p className="truncate text-xs text-[var(--color-accent-primary)]">
                              {candidate.title}
                            </p>
                          )}
                          <p className="truncate font-semibold text-[var(--color-text-primary)]">
                            {candidate?.name ?? entry.candidateEmail}
                          </p>
                        </div>
                        <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                          {t('cuttysark.results.votes', { count: entry.votes })}
                        </span>
                        {entry.advancing && (
                          <Badge variant="success">{t('cuttysark.results.advancing')}</Badge>
                        )}
                        {showTie && entry.tiedAtBoundary && (
                          <Badge variant="warning">{t('cuttysark.results.tied')}</Badge>
                        )}
                      </div>
                      {entry.comments.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-[var(--color-text-muted)]">
                            {t('cuttysark.results.comments')}
                          </p>
                          <ul className="mt-1 flex flex-wrap gap-1.5">
                            {entry.comments.map((comment, i) => (
                              <li
                                // 匿名コメントは同文が重なりうるので並び順を併用する
                                key={`${String(i)}-${comment}`}
                                className="rounded-full bg-[var(--color-bg-hover)] px-2.5 py-1 text-xs text-[var(--color-text-secondary)]"
                              >
                                {comment}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
